// Shared logic for "which cases can the clinician pick from?" — used by
// the patient-header "Active Plan Case" dropdown (ActivePlanCaseSelector)
// and the EMR import flow. Produces the ordered, de-duped case option list.
//
// Inputs:
//   percixCaseIds   the patient's existing Percix Case.case_id values
//                   (typically includes 'NA-portal' for legacy patients,
//                    plus any case_ids the clinician has previously used).
//   webptCases      WebPT-side cases returned by the listPatientCases
//                   recipe (undefined when the patient isn't EMR-connected
//                   or the extension isn't running). May overlap with the
//                   Percix list by id.
//
// Output: ordered, de-duped options.

// The legacy Case.case_id auto-created by useAddMandatoryCase for every
// pre-EMR patient. Surfaces nowhere in the UI — see displayCaseId below.
// We keep writing it for new patients too (so backend stays consistent),
// but the UI renders it as DEFAULT_NEW_PATIENT_CASE_ID instead.
//
// Wait — read carefully: as of Step 7 v3, useAddMandatoryCase creates
// '001' for NEW patients. Legacy patients still have 'NA-portal' rows in
// the DB; we display those as '001' too. Both states map to the same
// '001' string in the UI.
export const LEGACY_CASE_ID = 'NA-portal';
export const DEFAULT_NEW_PATIENT_CASE_ID = '001';

export type WebPTCase = {
  CaseID: number | string;
  CaseTitle?: string;
  // WebPT's objPatientInfo blob emits each diagnosis with BOTH casings —
  // `code`/`Description` alongside `ICD9`/`RecordID` etc. We read the
  // lowercase pair first and fall back to the capitalised one so either
  // shape round-trips (verified against a live patient-edit page).
  Diagnosis?: Array<{
    code?: string;
    description?: string;
    Description?: string;
  }>;
  // Cause code (e.g. 'SI' = Sports Injury). Mapped to the human label
  // via WEBPT_RELATED_CAUSE_LABELS below before display/persist.
  RelatedCause?: string;
  // Pre-formatted "LastName, FirstName CredentialCode" — what WebPT's
  // cases table renders directly.
  PhysicianIDName?: string;
  TherapistIDName?: string;
};

// WebPT's RelatedCause is stored as a short code (defined in WebPT's
// patient-edit page JS as `relatedCausesMap`). Map to the human label
// before persisting on Case.additional_info — clinicians see the same
// string everywhere.
export const WEBPT_RELATED_CAUSE_LABELS: Record<string, string> = {
  AA: 'Auto Accident (At Fault)',
  AF: 'Auto Accident (No Fault)',
  FA: 'Fall',
  AB: 'Abuse',
  AP: 'Another Party Responsible',
  EM: 'Employment Injury',
  SI: 'Sports Injury',
  SU: 'Surgery',
  OA: 'Other Accident',
  NA: 'None of the Above',
};

// Extract the per-case clinical fields from a WebPTCase into the
// CaseAdditionalInfo shape the Percix Case row stores.
export function webptCaseToAdditionalInfo(c: WebPTCase): {
  name?: string;
  icd10_codes?: string[];
  icd10_descriptions?: string[];
  cause?: string;
  therapist_name?: string;
  physician_name?: string;
  emr_case_id?: string;
} {
  // Keep codes and descriptions INDEX-ALIGNED: walk the diagnoses once and
  // only keep entries that have a code, pushing that entry's description (or
  // '' when it has none) so codes[i] always describes descriptions[i].
  // Filtering the two lists independently would silently mis-pair them.
  const icd10Codes: string[] = [];
  const icd10Descriptions: string[] = [];
  (c.Diagnosis ?? []).forEach((d) => {
    const code = d?.code;
    if (typeof code !== 'string' || code.length === 0) {
      return;
    }
    icd10Codes.push(code);
    icd10Descriptions.push((d?.description ?? d?.Description ?? '').trim());
  });
  const cause = c.RelatedCause
    ? WEBPT_RELATED_CAUSE_LABELS[c.RelatedCause] ?? c.RelatedCause
    : undefined;
  return {
    name: c.CaseTitle?.trim() || undefined,
    icd10_codes: icd10Codes.length ? icd10Codes : undefined,
    // Only store descriptions when at least one is non-empty — an array of
    // empty strings carries no information and would just bloat the jsonb.
    icd10_descriptions: icd10Descriptions.some((d) => d.length > 0)
      ? icd10Descriptions
      : undefined,
    cause,
    therapist_name: c.TherapistIDName?.trim() || undefined,
    physician_name: c.PhysicianIDName?.trim() || undefined,
    emr_case_id: String(c.CaseID),
  };
}

export type CaseOption = {
  value: string;
  label: string;
  // Source attribution. 'percix' = already a Case row; 'webpt' = WebPT
  // case the patient is linked to but not yet a Percix Case.
  source: 'percix' | 'webpt';
};

// Maps a stored Case.case_id to the string the clinician sees in the
// UI. The only mapping today: 'NA-portal' → '001'. Everything else is
// passed through unchanged. Used both for rendering option labels and
// for the duplicate-id guard in the header's "Add new case" affordance.
export function displayCaseId(caseId: string): string {
  if (caseId === LEGACY_CASE_ID) {
    return DEFAULT_NEW_PATIENT_CASE_ID;
  }
  return caseId;
}

// True when two stored case_ids refer to the same logical case as the
// clinician perceives it. Used for filtering case-scoped data (notes,
// movements, plans) when the user's selectedCaseId might be 'NA-portal'
// but the data row has '001' (or vice versa). All other case_ids only
// match themselves.
export function sameCase(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) {
    return false;
  }
  if (a === b) {
    return true;
  }
  return displayCaseId(a) === displayCaseId(b);
}

function labelForWebPT(c: WebPTCase): string {
  const id = displayCaseId(String(c.CaseID));
  const title = c.CaseTitle?.trim();
  const dx = c.Diagnosis?.[0]?.code;
  if (title && dx) {
    return `${id} — ${title} (${dx})`;
  }
  if (title) {
    return `${id} — ${title}`;
  }
  return id;
}

// Composes a label given a case_id and an optional name. If the name
// is present, the label is "id — name", otherwise just "id". Used for
// both Percix-side and WebPT-side options.
function labelWithName(caseId: string, name: string | undefined): string {
  const id = displayCaseId(caseId);
  const trimmed = name?.trim();
  if (trimmed) {
    return `${id} — ${trimmed}`;
  }
  return id;
}

// Minimal shape buildCaseOptions reads from a Percix Case. Caller can
// pass full CaseDto[] (the common case) or a stripped-down list.
export type PercixCaseLike = {
  case_id: string;
  additional_info?: { name?: string };
};

export function buildCaseOptions(
  percixCases: PercixCaseLike[],
  webptCases: WebPTCase[] | undefined,
): CaseOption[] {
  // De-dupe by DISPLAY label, not raw value. Both 'NA-portal' (legacy)
  // and '001' (new) render as '001' — if a patient has both rows in the
  // DB (which happens for test users created during the v2/v3 deploy
  // window), we want a single '001' option. Prefer the literal '001' so
  // future plan/note saves target the new convention.
  const sortedCases = [...percixCases].sort((a, b) => {
    if (a.case_id === DEFAULT_NEW_PATIENT_CASE_ID) {
      return -1;
    }
    if (b.case_id === DEFAULT_NEW_PATIENT_CASE_ID) {
      return 1;
    }
    return 0;
  });

  const options: CaseOption[] = [];
  const seenValues = new Set<string>();
  const seenDisplays = new Set<string>();

  const pushPercix = (percixCase: PercixCaseLike) => {
    const id = percixCase.case_id;
    if (seenValues.has(id) || seenDisplays.has(displayCaseId(id))) {
      return;
    }
    // Prefer the Percix-stored name. If absent (legacy case with no
    // name set), fall back to the WebPT label so we still surface
    // something descriptive when a WebPT case is in scope.
    const name = percixCase.additional_info?.name;
    const webptMatch = webptCases?.find((c) => String(c.CaseID) === id);
    const label = name
      ? labelWithName(id, name)
      : webptMatch
      ? labelForWebPT(webptMatch)
      : displayCaseId(id);
    options.push({
      value: id,
      label,
      source: 'percix',
    });
    seenValues.add(id);
    seenDisplays.add(displayCaseId(id));
  };

  // 1. Default-display ('001') first if either form is present. The sort
  //    above guarantees literal '001' is visited before 'NA-portal', so
  //    '001' wins the dedupe race when both exist.
  for (const c of sortedCases) {
    if (displayCaseId(c.case_id) === DEFAULT_NEW_PATIENT_CASE_ID) {
      pushPercix(c);
    }
  }

  // 2. Other Percix cases.
  for (const c of sortedCases) {
    pushPercix(c);
  }

  // 3. WebPT cases not yet imported, sorted by id for stable order.
  if (webptCases) {
    const remaining = webptCases
      .filter(
        (c) =>
          !seenValues.has(String(c.CaseID)) &&
          !seenDisplays.has(displayCaseId(String(c.CaseID))),
      )
      .sort((a, b) => String(a.CaseID).localeCompare(String(b.CaseID)));
    for (const c of remaining) {
      const id = String(c.CaseID);
      options.push({
        value: id,
        label: labelForWebPT(c),
        source: 'webpt',
      });
      seenValues.add(id);
      seenDisplays.add(displayCaseId(id));
    }
  }

  return options;
}

// Resolves the form-state initial value per the Step 7 default rule:
//   - if selectedCaseId is non-null/empty, use it;
//   - else if '001' exists in the patient's Percix cases, use it (the new
//     convention; preferred when both legacy and new forms are present);
//   - else if NA-portal exists, use it (rendered as '001');
//   - else first Percix case;
//   - else NA-portal (defensive — useAddMandatoryCase normally guarantees
//     at least one Percix case exists).
export function defaultCaseId(
  selectedCaseId: string | null | undefined,
  percixCaseIds: string[],
): string {
  if (selectedCaseId) {
    return selectedCaseId;
  }
  if (percixCaseIds.includes(DEFAULT_NEW_PATIENT_CASE_ID)) {
    return DEFAULT_NEW_PATIENT_CASE_ID;
  }
  if (percixCaseIds.includes(LEGACY_CASE_ID)) {
    return LEGACY_CASE_ID;
  }
  if (percixCaseIds.length > 0 && percixCaseIds[0]) {
    return percixCaseIds[0];
  }
  return LEGACY_CASE_ID;
}

// Returns null if the proposed new case_id can be added; otherwise an
// error message explaining why not. Considers existing Percix cases AND
// their displayed form — so a legacy patient with case_id='NA-portal'
// (displayed as '001') will reject an attempt to add a case literally
// named '001'.
export function validateNewCaseId(
  proposed: string,
  existingPercixCaseIds: string[],
): string | null {
  const trimmed = proposed.trim();
  if (!trimmed) {
    return 'Case ID cannot be empty';
  }
  for (const existing of existingPercixCaseIds) {
    if (existing === trimmed || displayCaseId(existing) === trimmed) {
      return `Case ${displayCaseId(trimmed)} already exists`;
    }
  }
  return null;
}
